/**
 * @file index.tsx
 *
 * @fileoverview Creates a ripple effect to be used inside buttons and other
 * clickable elements.
 */

import { useEffect, useState } from "react";
import { ColorsProps, generateColorClasses } from "../../../theme/colors";
import styles from "./ripple.module.scss";

const useDebouncedRippleCleanUp = (rippleCount, duration, cleanUpFunction) => {
  useEffect(() => {
    let bounce: any = null;
    if (rippleCount > 0) {
      clearTimeout(bounce);

      bounce = setTimeout(() => {
        cleanUpFunction();
        clearTimeout(bounce);
      }, duration * 4);
    }

    return () => clearTimeout(bounce);
  }, [rippleCount, duration, cleanUpFunction]);
};

type RippleProps = ColorsProps & {
  /**
   * The duration for the ripple effect.
   *
   * @default "850"
   */
  duration?: number;
};

export const Ripple = ({ duration = 850, ...props }: RippleProps) => {
  const [rippleArray, setRippleArray] = useState([]);

  useDebouncedRippleCleanUp(rippleArray.length, duration, () => {
    setRippleArray([]);
  });

  const addRipple = (event) => {
    const rippleContainer = event.currentTarget.getBoundingClientRect();
    const size =
      rippleContainer.width > rippleContainer.height
        ? rippleContainer.width
        : rippleContainer.height;
    const x = event.pageX - rippleContainer.x - size / 2;
    const y = event.pageY - rippleContainer.y - size / 2;
    const newRipple = {
      x,
      y,
      size,
    };

    // @ts-ignore.
    setRippleArray([...rippleArray, newRipple]);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.ripple}
      onMouseDown={addRipple}
    >
      {rippleArray.length > 0 &&
        rippleArray.map((ripple: any, index) => {
          return (
            <span
              key={index}
              style={{
                animationDuration: `${duration}ms`,
                top: ripple.y,
                left: ripple.x,
                width: ripple.size,
                height: ripple.size,
              }}
              className={`
                ${generateColorClasses(props, styles)}
              `.trim()}
            />
          );
        })}
    </div>
  );
};
export default Ripple;
